import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { TextInput } from "../../../../components/inputs/textInput";
import { InfoComponent } from "../../../../components/notification/infoComponent";
import { AuthHeader } from "../../../../components/typography/authHeader";
import {
  useAdminTwoFaMutation,
  useVerifyGoogleTwoFaMutation,
} from "../../../../redux/features/admin/auth/auth.api";
import { authSliceActions } from "../../../../redux/features/auth/auth.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { BaseApiErrorResponse } from "../../../../utils/types";
import { TwoFaSchema } from "../../../../utils/validations";

export const InitiateTwoFaPage = () => {
  const [adminTwoFa, { isLoading, isError, error }] = useAdminTwoFaMutation();
  const [
    verifyGoogleTwoFa,
    {
      isLoading: verifyGoogleLoading,
      isError: verifyGoogleIsError,
      error: verifyGoogleError,
    },
  ] = useVerifyGoogleTwoFaMutation();

  const { adminProfile: stateProfile } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: stateProfile?.email,
        twoFaCode: "",
      },
      validationSchema: TwoFaSchema,
      onSubmit: async (vals) => {
        const verifyResponse = await adminTwoFa({
          email: vals.email,
          twoFaCode: vals.twoFaCode,
        });

        if (verifyResponse.data?.status === "success") {
          const setupResponse = await verifyGoogleTwoFa({
            email: vals.email,
            twoFaCode: vals.twoFaCode,
          });

          if (setupResponse.data?.data.user) {
            const adminData = {
              ...setupResponse.data.data.user,
              twoFaBackupCodes: setupResponse.data.data.backupCodes,
            };

            dispatch(
              authSliceActions.setAdminBackUpCodes({
                profile: adminData,
                token: setupResponse.data.data.token,
                backupCodes: setupResponse.data.data.backupCodes,
              }),
            );

            navigate("/backup-codes");
          }
        }
      },
    });

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Set Up 2FA"
        text={
          <span>
            To enable 2FA, you need to install an autenticator app like Authy or
            Google Authenticator
          </span>
        }
      />

      <div className="mt-10 flex flex-col gap-5">
        {(isError || verifyGoogleIsError) && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              (verifyGoogleError as unknown as BaseApiErrorResponse)?.data
                ?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}
        <div className="flex items-center justify-center">
          {stateProfile?.twoFaQrCode && (
            <img
              src={stateProfile?.twoFaQrCode}
              alt="2FA QR Code"
              className="h-56 w-56"
            />
          )}
        </div>

        <div className="flex items-start gap-2.5">
          <p className="rounded-full border border-grey-200 px-3 py-1.5 text-sm font-bold text-grey-600">
            1
          </p>
          <p className="text-sm text-grey-600">
            Scan the QR code below with your authenticator app. If you can’t
            scan this QR, enter this setup key{" "}
            <span className="font-bold break-all whitespace-pre-wrap text-grey-600">{stateProfile?.twoFaSecret}</span> on
            the authenticator app instead.
          </p>
        </div>
        <div className="flex items-start gap-2.5">
          <p className="rounded-full border border-grey-200 px-3 py-1.5 text-sm font-bold text-grey-600">
            2
          </p>
          <p className="text-sm text-grey-600">
            Enter the 6-digit code generated by the authenticator app and click
            “Enable 2FA”
          </p>
        </div>
        <TextInput
          type="text"
          name="twoFaCode"
          value={values.twoFaCode}
          placeholder="Enter Verification Code"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="6-digit Verification Code"
          errors={errors.twoFaCode && touched.twoFaCode ? errors.twoFaCode : ""}
          autoFocus
          autoFocusOnMobile={false}
        />
        <Button
          type="submit"
          text="Enable 2FA"
          variation="primary"
          className="mt-5"
          loading={isLoading || verifyGoogleLoading}
          disabled={isLoading || verifyGoogleLoading}
        />
      </div>
    </form>
  );
};
